/**
 * Inbox detail panes (docs/ux/project-tab-spec.md §1.9 "Detail"): each kind shows its underlying
 * content, never the row's card again. Blocked environment → the machine's report (what failed,
 * the output, why it blocks, the fix commands, the affected run, when it was checked, re-check);
 * failed landing → the structured failure (phase, command, exit code, output, baseline, conflicts,
 * next action, retry); device/usage alert → the metric, forecast and remedies. Every pane says how
 * fresh its evidence is, and pins its actions at the bottom.
 */
import { Link } from '@tanstack/react-router'
import type { DeviceAlertReport } from '@viko/protocol'
import { Button } from '@viko/transcript-ui'
import { Copy, RefreshCw, Wrench } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { useBackend } from '../../lib/backend.js'
import { actOnCard } from '../../lib/inbox-actions.js'
import { ago, type Freshness, stallReason } from '../../lib/inbox-freshness.js'
import { landGrantOpt, useLandGrantPicker } from '../../lib/land-grant.js' // land-grant
import { useLandings, useMachines, useRuns } from '../../lib/queries.js'
import type { DoctorCheck, InboxItem, Landing, Machine, MachineBlocker } from '../../lib/types.js'
import { FailureDetail } from '../LandSheet.js'
import { useLocked } from '../lock/ContentLock.js'
import { errorText, useToast } from '../ui.js'
import '../decision/decision.css'

const STATE_LABEL: Record<Freshness['state'], string> = {
  current: 'Current',
  resolved: 'Resolved',
  waiting: 'Waiting for a report',
  outdated: 'May be outdated',
}

function Pane({
  kicker,
  headline,
  freshness,
  facts,
  children,
  actions,
  kind,
}: {
  kicker: string
  headline: string
  freshness?: Freshness | undefined
  facts: [string, ReactNode][]
  children: ReactNode
  actions: ReactNode
  kind: string
}) {
  return (
    <article className="dv" aria-label={headline} data-detail={kind}>
      <div className="dv__scroll">
        <header className="dv__head">
          <ul className="dv__chips" aria-label="Status">
            <li className="dv-chip">{kicker}</li>
            {freshness ? (
              <li
                className={`dv-chip${freshness.state === 'current' ? ' is-hot' : freshness.state === 'resolved' ? '' : ' is-risky'}`}
                data-freshness={freshness.state}
              >
                {STATE_LABEL[freshness.state]}
              </li>
            ) : null}
          </ul>
          <p className="dv__question">{headline}</p>
          {freshness ? (
            <p className="dv__why" role="status">
              {freshness.text}
            </p>
          ) : null}
          <dl className="dv__facts">
            {facts.map(([k, v]) => (
              <div key={k}>
                <dt>{k}</dt>
                <dd>{v}</dd>
              </div>
            ))}
          </dl>
        </header>
        {children}
      </div>
      <section className="dv-bar" aria-label="Actions">
        <div className="dv-bar__row">{actions}</div>
      </section>
    </article>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="dv-sec" aria-label={title}>
      <div className="dv-sec__h">
        <h3>{title}</h3>
      </div>
      {children}
    </section>
  )
}

function Output({ lines }: { lines: readonly string[] }) {
  return (
    <pre className="dv-ex" data-kind="log">
      {lines.join('\n')}
    </pre>
  )
}

function CopyButton({ text, label = 'Copy' }: { text: string; label?: string }) {
  const toast = useToast()
  return (
    <Button
      size="sm"
      variant="ghost"
      icon={<Copy size={13} />}
      onClick={() =>
        void navigator.clipboard
          ?.writeText(text)
          .then(() => toast('Copied'))
          .catch(() => toast('Copy is not available here', 'error'))
      }
    >
      {label}
    </Button>
  )
}

function useAct() {
  const b = useBackend()
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  const run = async (fn: () => Promise<unknown>, ok?: string) => {
    setBusy(true)
    try {
      await fn()
      if (ok) toast(ok)
      return true
    } catch (e) {
      toast(errorText(e), 'error')
      return false
    } finally {
      setBusy(false)
    }
  }
  return { b, busy, run }
}

const when = (ts: number | undefined, now: number) =>
  ts
    ? `${new Date(ts).toLocaleString([], { weekday: 'short', hour: 'numeric', minute: '2-digit' })} (${ago(now - ts)})`
    : '—'

// ---- blocked environment -----------------------------------------------------------------------

function FixRow({ check, machine }: { check: DoctorCheck; machine: Machine }) {
  const { b, busy, run } = useAct()
  const cmd = check.command
  return (
    <li className="dv-ev__item" data-doctor-check={check.checkId ?? check.id}>
      <div className="dv-ev__top">
        <span className={`dv-tag ${check.status === 'fail' ? 'is-fail' : ''}`}>
          {check.status === 'fail' ? 'Failing' : 'Warning'}
        </span>
        <span className="dv-ev__label">{check.title}</span>
      </div>
      {check.detail ? <p className="dv-muted">{check.detail}</p> : null}
      {check.fix ? <p className="dv-muted">Fix: {check.fix}</p> : null}
      {cmd ? <code className="dv-ev__ref">{cmd}</code> : null}
      <div className="dv-bar__row">
        {check.autoFix && check.checkId ? (
          <Button
            size="sm"
            variant="primary"
            icon={<Wrench size={13} />}
            disabled={busy}
            onClick={() =>
              void run(
                () => b.dispatch('doctor.fix', machine.id, { checkId: check.checkId as string }),
                `Fix sent to ${machine.name}`,
              )
            }
          >
            Run fix
          </Button>
        ) : null}
        {cmd ? <CopyButton text={cmd} label="Copy command" /> : null}
      </div>
    </li>
  )
}

export function BlockerDetail({
  item,
  freshness,
  onDone,
}: {
  item: InboxItem
  freshness?: Freshness | undefined
  onDone?: (() => void) | undefined
}) {
  const { b, busy, run } = useAct()
  const locked = useLocked()
  const runs = useRuns()
  const now = Date.now()
  const body = (item.body ?? {}) as Partial<MachineBlocker> & { machineId?: string }
  const blocker: Partial<MachineBlocker> = freshness?.blocker ?? body
  const machine = freshness?.machine as Machine | undefined
  const runId = blocker.runId ?? item.runId
  const r = runs.data?.find((x) => x.runId === runId)
  const reason = stallReason(blocker.cause ?? item.category)
  const opaque = !freshness?.blocker && body.title === 'details are in the machine report'
  const headline = opaque
    ? `${r?.title ?? 'A run'} is blocked by its environment`
    : (reason ?? blocker.title ?? 'A run is blocked by its environment')
  const checks = (machine?.doctor ?? []).filter(
    (c) =>
      c.status !== 'ok' &&
      (!blocker.doctorCheck || c.checkId === blocker.doctorCheck || c.status === 'fail'),
  )
  const machineId = machine?.id ?? body.machineId ?? item.machineId
  const recheck = () =>
    machineId
      ? void run(
          () => b.dispatch('doctor.run', machineId, {}),
          `Re-checking ${machine?.name ?? 'the machine'}`,
        )
      : undefined
  return (
    <Pane
      kind="blocked"
      kicker="Blocked: environment"
      headline={headline}
      freshness={freshness}
      facts={[
        [
          'What',
          opaque
            ? 'Details are in the machine report (not on this device yet)'
            : (blocker.title ?? '—'),
        ],
        [
          'Why it blocks',
          blocker.autoFix
            ? 'Viko is fixing this automatically; the run resumes when it clears.'
            : 'This run stays paused until it is fixed; other runs keep going.',
        ],
        ['Machine', machine?.name ?? machineId ?? '—'],
        [
          'Run',
          runId ? (
            <Link key="r" className="dv-link" to="/s/$runId" params={{ runId }}>
              {r?.title ?? runId}
            </Link>
          ) : (
            '—'
          ),
        ],
        ...(r?.taskTitle ? ([['Task', r.taskTitle]] as [string, ReactNode][]) : []),
        ['Detected', when(blocker.detectedAt ?? item.createdAt, now)],
        ['Last checked', freshness?.checkedAt ? when(freshness.checkedAt, now) : 'No report since'],
      ]}
      actions={
        <>
          <Button
            size="sm"
            variant="primary"
            icon={<RefreshCw size={13} />}
            disabled={busy || !machineId}
            onClick={recheck}
          >
            Re-check now
          </Button>
          {runId ? (
            <Link className="vk-btn vk-btn--secondary vk-btn--sm" to="/s/$runId" params={{ runId }}>
              <span>Open run</span>
            </Link>
          ) : null}
          <span className="dv-bar__grow" />
          <Button
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={() =>
              void run(() => actOnCard(b.api, item, 'done'), 'Marked done').then(
                (ok) => ok && onDone?.(),
              )
            }
          >
            It's fixed
          </Button>
        </>
      }
    >
      {blocker.evidence || blocker.logTail?.length ? (
        <Section title="What the run printed">
          {blocker.evidence ? <p className="dv-muted">Matched: {blocker.evidence}</p> : null}
          {blocker.logTail?.length ? <Output lines={blocker.logTail} /> : null}
        </Section>
      ) : null}
      <Section title="How to fix it">
        {blocker.remediation && !opaque ? <p className="dv-muted">{blocker.remediation}</p> : null}
        {blocker.command ? (
          <div className="dv-ev__item">
            <code className="dv-ev__ref">{blocker.command}</code>
            <div className="dv-bar__row">
              <CopyButton text={blocker.command} label="Copy command" />
            </div>
          </div>
        ) : null}
        {checks.length && machine ? (
          <ul className="dv-ev" aria-label={`Checks failing on ${machine.name}`}>
            {checks.map((c) => (
              <FixRow key={c.id} check={c} machine={machine} />
            ))}
          </ul>
        ) : opaque ? (
          <p className="dv-muted">
            {locked
              ? 'Re-check the machine to get a fresh report, or unlock this browser to read the one it has.'
              : 'Re-check the machine to get a fresh report.'}
          </p>
        ) : !blocker.command ? (
          <p className="dv-muted">
            No automatic fix is known. Open the run to see where it stopped.
          </p>
        ) : null}
      </Section>
    </Pane>
  )
}

// ---- failed landing ------------------------------------------------------------------------------

export function LandingFailureDetail({
  item,
  freshness,
  onDone,
}: {
  item: InboxItem
  freshness?: Freshness | undefined
  onDone?: (() => void) | undefined
}) {
  const { b, busy, run } = useAct()
  const grantFor = useLandGrantPicker() // land-grant
  const landings = useLandings()
  const now = Date.now()
  const body = (item.body ?? {}) as { landingId?: string; error?: string }
  const landing: Landing | undefined =
    freshness?.landing ?? landings.data?.find((l) => l.id === body.landingId)
  const f = landing?.failure
  const machineId = landing?.machineId ?? item.machineId
  const retry = (anyway = false) =>
    landing?.workstreamId && machineId
      ? void run(
          () =>
            b.dispatch(
              'land.merge',
              machineId,
              {
                workstreamId: landing.workstreamId as string,
                ...(landing.runId ? { runId: landing.runId } : {}),
                ...(landing.branch ? { branch: landing.branch } : {}),
                target: 'main',
                ...(anyway ? { acceptBaselineFailures: true } : {}),
              },
              {
                summary: `Land ${landing.branch ?? 'this branch'} into main`,
                // land-grant: a retry within a standing grant needs no tap; "anyway" always does.
                ...(anyway
                  ? {}
                  : landGrantOpt(
                      grantFor(
                        {
                          projectId: landing.projectId ?? null,
                          workstreamId: landing.workstreamId ?? null,
                        },
                        machineId,
                      ),
                    )),
              },
            ),
          'Landing queued',
        ).then((ok) => ok && onDone?.())
      : undefined
  const failed = (landing?.recipe ?? []).filter((x) => x.status === 'fail')
  return (
    <Pane
      kind="landing"
      kicker="Landing failed"
      headline={
        f
          ? `${f.phaseLabel}: ${f.message}`
          : `Landing ${landing?.branch ?? ''} failed: ${landing?.reason ?? landing?.error ?? body.error ?? 'no reason reported'}`
      }
      freshness={freshness}
      facts={[
        ['Branch', landing?.branch ? <code key="b">{landing.branch}</code> : '—'],
        ['Phase', f?.phaseLabel ?? landing?.step ?? '—'],
        ...(f?.command
          ? ([
              [
                'Command',
                <code key="c">
                  {f.command}
                  {f.exitCode !== undefined ? ` (exit ${f.exitCode})` : ''}
                </code>,
              ],
            ] as [string, ReactNode][])
          : []),
        [
          'Fails on main too',
          (f?.baselineFailing ?? landing?.baselineFailing) === undefined
            ? 'Not checked'
            : (f?.baselineFailing ?? landing?.baselineFailing)
              ? 'Yes: not caused by this branch'
              : 'No: caused by this branch',
        ],
        ...(f?.files?.length
          ? ([['Conflicting files', f.files.join(', ')]] as [string, ReactNode][])
          : []),
        ['Failed', when(landing?.updatedAt ?? item.createdAt, now)],
        ...(f?.nextAction ? ([['Next', f.nextAction]] as [string, ReactNode][]) : []),
      ]}
      actions={
        <>
          <Button
            size="sm"
            variant="primary"
            disabled={busy || !landing?.workstreamId}
            onClick={() => retry(false)}
          >
            Retry land
          </Button>
          {(f?.baselineFailing ?? landing?.baselineFailing) ? (
            <Button size="sm" variant="secondary" disabled={busy} onClick={() => retry(true)}>
              Land anyway (passkey)
            </Button>
          ) : null}
          {landing?.runId ? (
            <Link
              className="vk-btn vk-btn--secondary vk-btn--sm"
              to="/s/$runId"
              params={{ runId: landing.runId }}
            >
              <span>Open run</span>
            </Link>
          ) : null}
          <span className="dv-bar__grow" />
          <Button
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={() =>
              void run(() => actOnCard(b.api, item, 'done'), 'Dismissed').then(
                (ok) => ok && onDone?.(),
              )
            }
          >
            Dismiss
          </Button>
        </>
      }
    >
      {landing ? (
        <Section title="Failure">
          {f ? (
            <FailureDetail landing={landing} />
          ) : (
            <p className="dv-muted">The landing reported no structured failure.</p>
          )}
        </Section>
      ) : null}
      {failed.length ? (
        <Section title="Failing recipe steps">
          <ul className="dv-ev">
            {failed.map((x) => (
              <li key={x.command} className="dv-ev__item">
                <div className="dv-ev__top">
                  <span className="dv-tag is-fail">Failed</span>
                  <code className="dv-ev__label">{x.command}</code>
                  {x.exitCode !== undefined ? (
                    <span className="dv-muted">exit {x.exitCode}</span>
                  ) : null}
                </div>
                {x.baseline === 'fails' ? <p className="dv-muted">Fails on main too.</p> : null}
                {x.outputTail ? <Output lines={x.outputTail.split('\n').slice(-40)} /> : null}
              </li>
            ))}
          </ul>
        </Section>
      ) : null}
    </Pane>
  )
}

// ---- device / usage alert --------------------------------------------------------------------------

const fmt = (v: number, unit: 'pct' | 'bytes') =>
  unit === 'bytes' ? `${(v / 1024 ** 3).toFixed(1)} GB` : `${v}%`

export function AlertDetail({
  item,
  onDone,
}: {
  item: InboxItem
  onDone?: (() => void) | undefined
}) {
  const { b, busy, run } = useAct()
  const machines = useMachines()
  const a = item.body as DeviceAlertReport
  const now = Date.now()
  const m = a.metric
  const machineId = item.machineId ?? (item.body as { machineId?: string }).machineId
  const machine = machines.data?.find((x) => x.id === machineId)
  return (
    <Pane
      kind="alert"
      kicker={
        a.alertKind === 'battery'
          ? 'Battery'
          : a.alertKind === 'disk'
            ? 'Disk space'
            : a.alertKind === 'permission'
              ? 'macOS permission'
              : a.alertKind === 'wait'
                ? 'Long job'
                : 'Usage limit'
      }
      headline={a.title}
      facts={[
        ['Machine', machine?.name ?? machineId ?? '—'],
        ...(a.runId ? ([['Run', a.runId]] as [string, ReactNode][]) : []), // machine-resources
        ...(m
          ? ([
              [
                'Now',
                `${fmt(m.value, m.unit)}${m.charging ? ' (charging)' : ''}${m.volume ? ` on ${m.volume}` : ''}${m.account ? ` for ${m.account}` : ''}`,
              ],
              ['Threshold', fmt(m.threshold, m.unit)],
            ] as [string, ReactNode][])
          : []),
        ...(a.forecast
          ? ([
              [
                'Forecast',
                `Runs out ${when(a.forecast.exhaustsAt, now)}${a.forecast.resetsAt ? `; resets ${when(a.forecast.resetsAt, now)}` : ''}`,
              ],
            ] as [string, ReactNode][])
          : []),
        ['New runs', a.parked ? 'Held until this clears' : 'Still start'],
        ['Raised', when(a.raisedAt, now)],
      ]}
      actions={
        <>
          <span className="dv-bar__grow" />
          <Button
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={() =>
              void run(() => actOnCard(b.api, item, 'dismiss'), 'Dismissed').then(
                (ok) => ok && onDone?.(),
              )
            }
          >
            Dismiss
          </Button>
        </>
      }
    >
      <Section title="What is happening">
        <p className="dv-muted">{a.detail}</p>
      </Section>
      {a.remedies.length ? (
        <Section title="What you can do">
          <ul className="dv-ev">
            {a.remedies.map((r) => (
              <li key={r.id} className="dv-ev__item">
                <span className="dv-ev__label">{r.label}</span>
                {r.detail ? <p className="dv-muted">{r.detail}</p> : null}
                {r.needsConsent ? <p className="dv-muted">Asks for your approval first.</p> : null}
              </li>
            ))}
          </ul>
        </Section>
      ) : null}
    </Pane>
  )
}
